import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './VastAdManager.css';
import React, { useState, useEffect, useCallback, useRef } from 'react';

const VastAdManager = () => {
}

const useAdManager = () => {
  const [preloadedAds, setPreloadedAds] = useState([]);
  const [isPreloading, setIsPreloading] = useState(false);
  const [networkStatus, setNetworkStatus] = useState({});
  const [lastRequestTimes, setLastRequestTimes] = useState({});
  const loadingRequestsRef = useRef(new Set());
  
  // **NOUVEAU: États pour gérer le rechargement quand cache vide**
  const [lastEmptyCacheTime, setLastEmptyCacheTime] = useState(null);
  const [emptyRetryTimer, setEmptyRetryTimer] = useState(null);
  
  // **NOUVEAU: Suivi des URLs vidéo déjà jouées avec timestamps**
  const playedVideoUrlsRef = useRef(new Map());
  
  const [adSettings] = useState({
    frequency: 3, // Afficher une pub toutes les 3 vidéos
    maxPreload: 5, // Nombre maximum de pubs en cache
    minTimeBetweenRequests: 30000, // 30 secondes minimum entre requêtes par réseau
    maxRetries: 2, // Nombre maximum de tentatives par réseau
    timeout: 15000, // Timeout par requête (15 secondes)
    maxWrapperDepth: 5, // Profondeur maximum des wrappers pour éviter les boucles infinies
    
    // **NOUVEAU: Paramètres pour le rechargement automatique quand cache vide**
    emptyRetryDelay: 30000, // 30 secondes avant de retry quand cache vide
    maxEmptyRetries: 10, // Maximum 10 tentatives quand cache vide
    
    // **NOUVEAU: Paramètre anti-répétition des URLs vidéo**
    videoUrlCooldown: 5 * 60 * 1000, // 5 minutes en millisecondes
    
    // Liste des réseaux publicitaires VAST
    vastNetworks: [
      {
        name: "HillTopAds",
        url: "https://briefsection.com/dUmWF.zcd_G/Nsv/ZVGpUC/zenmH9tu_ZTUKlHkVP-TSYO0SN/jbIT5jMoTaUVt/NcjRQ/2/MZj/knx/NCgw",
        priority: 2,
        enabled: false
      },
      {
        name: "ExoClick",
        url: "https://s.magsrv.com/v1/vast.php?idzone=5659090",
        priority: 5,
        enabled: false
      },
      {
        name: "PropellerAds",
        url: "https://delivery.propellerads.com/vast/?zoneId=1234567",
        priority: 3,
        enabled: false // Désactivé par défaut car pas d'ID valide
      },
      {
        name: "AdCash",
        url: "https://vast.adcash.com/vast?zoneId=1234567",
        priority: 4,
        enabled: false // Désactivé par défaut car pas d'ID valide
      },

      // Contenait du porn meme après les avoir enlever 1 juillet, faudrait peut-être retester
      {
        name: "TrafficStars",
        url: "https://runative-syndicate.com/do2/7e9361d9a7b54b7b80c46ebe72664c4e/vast",
        priority: 1,
        enabled: false
      }
    ]
  });

  // Initialiser le statut des réseaux
  useEffect(() => {
    const initialStatus = {};
    const initialTimes = {};
    
    adSettings.vastNetworks.forEach(network => {
      initialStatus[network.name] = {
        lastSuccess: null,
        lastError: null,
        consecutiveErrors: 0,
        totalRequests: 0,
        successfulRequests: 0,
        enabled: network.enabled
      };
      initialTimes[network.name] = 0;
    });
    
    setNetworkStatus(initialStatus);
    setLastRequestTimes(initialTimes);
  }, []);

  // **NOUVEAU: Fonction pour vérifier si une URL vidéo peut être utilisée**
  const canUseVideoUrl = useCallback((videoUrl) => {
    if (!videoUrl) return false;
    
    const now = Date.now();
    const lastPlayedTime = playedVideoUrlsRef.current.get(videoUrl);
    
    if (!lastPlayedTime) {
      // URL jamais jouée, on peut l'utiliser
      return true;
    }
    
    const timeSinceLastPlay = now - lastPlayedTime;
    const canUse = timeSinceLastPlay >= adSettings.videoUrlCooldown;
    
    if (!canUse) {
      const remainingCooldown = Math.ceil((adSettings.videoUrlCooldown - timeSinceLastPlay) / 1000);
      console.log(`🚫 URL vidéo en cooldown pour ${remainingCooldown}s:`, videoUrl.substring(0, 50) + '...');
    }
    
    return canUse;
  }, [adSettings.videoUrlCooldown]);

  // **NOUVEAU: Fonction pour marquer une URL vidéo comme jouée**
  const markVideoUrlAsPlayed = useCallback((videoUrl) => {
    if (!videoUrl) return;
    
    const now = Date.now();
    playedVideoUrlsRef.current.set(videoUrl, now);
    
    console.log(`📝 URL vidéo marquée comme jouée:`, videoUrl.substring(0, 50) + '...');
    
    // Nettoyer les anciennes entrées pour éviter une croissance infinie de la Map
    const cutoffTime = now - adSettings.videoUrlCooldown;
    for (const [url, timestamp] of playedVideoUrlsRef.current.entries()) {
      if (timestamp < cutoffTime) {
        playedVideoUrlsRef.current.delete(url);
      }
    }
  }, [adSettings.videoUrlCooldown]);

  // **NOUVEAU: Fonction pour nettoyer le cache des URLs jouées**
  const cleanupPlayedUrls = useCallback(() => {
    const now = Date.now();
    const cutoffTime = now - adSettings.videoUrlCooldown;
    let cleaned = 0;
    
    for (const [url, timestamp] of playedVideoUrlsRef.current.entries()) {
      if (timestamp < cutoffTime) {
        playedVideoUrlsRef.current.delete(url);
        cleaned++;
      }
    }
    
    if (cleaned > 0) {
      console.log(`🧹 ${cleaned} URLs vidéo nettoyées du cache de répétition`);
    }
  }, [adSettings.videoUrlCooldown]);

  // Obtenir les réseaux actifs triés par priorité
  const getActiveNetworks = useCallback(() => {
    return adSettings.vastNetworks
      .filter(network => {
        const status = networkStatus[network.name];
        return network.enabled && 
               status?.enabled !== false && 
               status?.consecutiveErrors < 3; // Désactiver après 3 erreurs consécutives
      })
      .sort((a, b) => {
        // Trier par priorité, puis par taux de succès
        if (a.priority !== b.priority) {
          return a.priority - b.priority;
        }
        
        const statusA = networkStatus[a.name];
        const statusB = networkStatus[b.name];
        
        if (statusA && statusB) {
          const successRateA = statusA.totalRequests > 0 ? statusA.successfulRequests / statusA.totalRequests : 0;
          const successRateB = statusB.totalRequests > 0 ? statusB.successfulRequests / statusB.totalRequests : 0;
          return successRateB - successRateA; // Meilleur taux en premier
        }
        
        return 0;
      });
  }, [adSettings.vastNetworks, networkStatus]);

  // Fusionner les événements de tracking de plusieurs niveaux
  const mergeTrackingEvents = (events1, events2) => {
    const merged = { ...events1 };
    
    Object.keys(events2).forEach(eventType => {
      if (merged[eventType]) {
        merged[eventType] = [...merged[eventType], ...events2[eventType]];
      } else {
        merged[eventType] = [...events2[eventType]];
      }
    });
    
    return merged;
  };

  // Fusionner les URLs de tracking
  const mergeTrackingUrls = (urls1, urls2) => {
    const filtered1 = (urls1 || []).filter(url => url && url.trim());
    const filtered2 = (urls2 || []).filter(url => url && url.trim());
    return [...filtered1, ...filtered2];
  };

  // Charger et résoudre les wrappers VAST
  const loadVastWithWrappers = async (vastUrl, networkName, depth = 0) => {
    if (depth >= adSettings.maxWrapperDepth) {
      throw new Error(`Profondeur maximum des wrappers atteinte (${adSettings.maxWrapperDepth})`);
    }

    console.log(`📡 Chargement VAST niveau ${depth} depuis ${networkName}: ${vastUrl.substring(0, 100)}...`);
    
    // Charger le XML VAST
    const xmlText = await loadVastViaIframe(vastUrl, `${networkName}_depth${depth}`);
    
    if (!xmlText || xmlText.trim().length === 0) {
      throw new Error('Réponse VAST vide');
    }

    // Parser le XML
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
    
    // Vérifier les erreurs XML
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('XML mal formé');
    }
    
    const adElements = xmlDoc.querySelectorAll('Ad');
    if (adElements.length === 0) {
      throw new Error('Aucune publicité trouvée dans la réponse VAST');
    }

    const resolvedAds = [];

    // Traiter chaque publicité
    for (const adElement of adElements) {
      const adId = adElement.getAttribute('id') || `${networkName}_${Date.now()}_${depth}`;
      
      // Vérifier si c'est un wrapper
      const wrapperElement = adElement.querySelector('Wrapper');
      const inlineElement = adElement.querySelector('InLine');
      
      if (wrapperElement) {
        console.log(`🔄 Wrapper détecté pour ${adId}, résolution...`);
        
        // Extraire les données du wrapper
        const wrapperData = extractWrapperData(wrapperElement, adId, networkName, depth);
        
        // Obtenir l'URL du VAST suivant
        const vastAdTagURI = wrapperElement.querySelector('VASTAdTagURI');
        if (!vastAdTagURI) {
          console.warn(`⚠️ Wrapper ${adId} sans VASTAdTagURI`);
          continue;
        }
        
        const nextVastUrl = vastAdTagURI.textContent.trim();
        if (!nextVastUrl) {
          console.warn(`⚠️ VASTAdTagURI vide pour ${adId}`);
          continue;
        }
        
        try {
          // Charger récursivement le VAST suivant
          const childAds = await loadVastWithWrappers(nextVastUrl, networkName, depth + 1);
          
          // Fusionner les données du wrapper avec celles des publicités enfants
          childAds.forEach(childAd => {
            const mergedAd = {
              ...childAd,
              id: `${adId}_${childAd.id}`,
              wrapperDepth: depth,
              wrapperChain: [...(childAd.wrapperChain || []), adId],
              
              // Fusionner les tracking events
              trackingEvents: mergeTrackingEvents(wrapperData.trackingEvents, childAd.trackingEvents),
              
              // Fusionner les autres URLs de tracking
              impressions: mergeTrackingUrls(wrapperData.impressions, childAd.impressions),
              clickTracking: mergeTrackingUrls(wrapperData.clickTracking, childAd.clickTracking),
              errors: mergeTrackingUrls(wrapperData.errors, childAd.errors),
              
              // Garder les données créatives de l'enfant, mais ajouter les infos du wrapper
              clickThrough: childAd.clickThrough || wrapperData.clickThrough,
              
              // Métadonnées
              wrapperData: [
                ...(childAd.wrapperData || []),
                {
                  depth,
                  adId,
                  impressions: wrapperData.impressions.length,
                  clickTracking: wrapperData.clickTracking.length,
                  trackingEvents: Object.keys(wrapperData.trackingEvents).length
                }
              ]
            };
            
            resolvedAds.push(mergedAd);
          });
          
        } catch (error) {
          console.error(`❌ Erreur résolution wrapper ${adId}:`, error.message);
          
          // Si échec du wrapper, continuer avec les autres pubs
          continue;
        }
        
      } else if (inlineElement) {
        console.log(`📺 Publicité inline trouvée: ${adId}`);
        
        // Parser la publicité inline normale avec test d'URL
        const inlineAd = await parseInlineAd(inlineElement, adId, networkName, depth);
        if (inlineAd) {
          resolvedAds.push(inlineAd);
        }
        
      } else {
        console.warn(`⚠️ Publicité ${adId} ni wrapper ni inline`);
      }
    }

    return resolvedAds;
  };

  // Extraire les données d'un wrapper VAST
  const extractWrapperData = (wrapperElement, adId, networkName, depth) => {
    const data = {
      impressions: [],
      clickTracking: [],
      errors: [],
      trackingEvents: {},
      clickThrough: null
    };

    // Extraire les impressions du wrapper
    const impressionElements = wrapperElement.querySelectorAll('Impression');
    data.impressions = Array.from(impressionElements)
      .map(el => el.textContent.trim())
      .filter(url => url);

    // Extraire les erreurs du wrapper
    const errorElements = wrapperElement.querySelectorAll('Error');
    data.errors = Array.from(errorElements)
      .map(el => el.textContent.trim())
      .filter(url => url);

    // Extraire les tracking events du wrapper
    const trackingElements = wrapperElement.querySelectorAll('Tracking');
    trackingElements.forEach(element => {
      const event = element.getAttribute('event');
      const url = element.textContent.trim();
      if (event && url) {
        if (!data.trackingEvents[event]) {
          data.trackingEvents[event] = [];
        }
        data.trackingEvents[event].push(url);
      }
    });

    // Extraire les clics du wrapper
    const clickTrackingElements = wrapperElement.querySelectorAll('ClickTracking');
    data.clickTracking = Array.from(clickTrackingElements)
      .map(el => el.textContent.trim())
      .filter(url => url);

    const clickThroughElement = wrapperElement.querySelector('ClickThrough');
    if (clickThroughElement) {
      data.clickThrough = clickThroughElement.textContent.trim();
    }

    console.log(`📊 Wrapper ${adId} tracking:`, {
      impressions: data.impressions.length,
      clickTracking: data.clickTracking.length,
      errors: data.errors.length,
      trackingEvents: Object.keys(data.trackingEvents).length
    });

    return data;
  };

  // Parser une publicité inline avec test d'URL
  const parseInlineAd = async (inlineElement, adId, networkName, depth) => {
    try {
      // Extraire l'URL de la vidéo avec priorité MP4
      const mediaFileElements = inlineElement.querySelectorAll('MediaFile');
      let videoUrl = null;
      let preferredFormat = null;
      
      // Chercher d'abord un MP4
      for (const mediaFile of mediaFileElements) {
        const type = mediaFile.getAttribute('type');
        const url = mediaFile.textContent.trim();
        
        if (type && type.includes('mp4') && url) {
          videoUrl = url;
          preferredFormat = 'mp4';
          break;
        }
      }
      
      // Si pas de MP4, prendre WebM
      if (!videoUrl) {
        for (const mediaFile of mediaFileElements) {
          const type = mediaFile.getAttribute('type');
          const url = mediaFile.textContent.trim();
          
          if (type && type.includes('webm') && url) {
            videoUrl = url;
            preferredFormat = 'webm';
            break;
          }
        }
      }
      
      // Sinon prendre le premier disponible
      if (!videoUrl && mediaFileElements.length > 0) {
        videoUrl = mediaFileElements[0].textContent.trim();
        const type = mediaFileElements[0].getAttribute('type');
        preferredFormat = type || 'unknown';
      }

      if (!videoUrl) {
        console.warn(`Aucun fichier média trouvé pour l'annonce ${adId} (${networkName})`);
        return null;
      }

      // **NOUVELLE VÉRIFICATION : Vérifier si cette URL peut être utilisée (cooldown)**
      if (!canUseVideoUrl(videoUrl)) {
        console.warn(`🚫 URL vidéo en cooldown, pub ${adId} ignorée:`, videoUrl.substring(0, 50) + '...');
        return null;
      }

      // **NOUVELLE ÉTAPE : TESTER ET PRÉPARER L'URL VIDÉO**
      console.log(`🔧 Préparation URL vidéo pour ${adId}...`);
      const finalVideoUrl = await testAndPrepareVideoUrl(videoUrl, adId, networkName);

      // Extraire la durée
      const durationElement = inlineElement.querySelector('Duration');
      let duration = null;
      if (durationElement) {
        const durationText = durationElement.textContent.trim();
        // Convertir format HH:MM:SS en secondes
        const parts = durationText.split(':');
        if (parts.length === 3) {
          duration = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
        }
      }

      // Extraire skipoffset
      const linearElement = inlineElement.querySelector('Linear');
      let skipOffset = 5; // Valeur par défaut
      if (linearElement) {
        const skipOffsetAttr = linearElement.getAttribute('skipoffset');
        if (skipOffsetAttr) {
          if (skipOffsetAttr.includes(':')) {
            const parts = skipOffsetAttr.split(':');
            if (parts.length === 3) {
              skipOffset = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
            }
          } else {
            // Format en secondes directement
            skipOffset = parseInt(skipOffsetAttr) || 5;
          }
        }
      }

      // Extraire les événements de tracking
      const trackingEvents = {};
      const trackingElements = inlineElement.querySelectorAll('Tracking');
      
      trackingElements.forEach(element => {
        const event = element.getAttribute('event');
        const url = element.textContent.trim();
        if (event && url) {
          if (!trackingEvents[event]) {
            trackingEvents[event] = [];
          }
          trackingEvents[event].push(url);
        }
      });

      // Extraire les URLs de clic
      const clickThroughElement = inlineElement.querySelector('ClickThrough');
      const clickThrough = clickThroughElement ? clickThroughElement.textContent.trim() : null;

      const clickTrackingElements = inlineElement.querySelectorAll('ClickTracking');
      const clickTracking = Array.from(clickTrackingElements).map(el => el.textContent.trim()).filter(url => url);

      // Extraire les impressions
      const impressionElements = inlineElement.querySelectorAll('Impression');
      const impressions = Array.from(impressionElements).map(el => el.textContent.trim()).filter(url => url);

      // Extraire les erreurs
      const errorElements = inlineElement.querySelectorAll('Error');
      const errors = Array.from(errorElements).map(el => el.textContent.trim()).filter(url => url);

      return {
        id: adId,
        network: networkName,
        videoUrl: finalVideoUrl, // URL FINALE TESTÉE ET PRÊTE
        originalVideoUrl: videoUrl, // URL originale pour debug
        format: preferredFormat,
        duration,
        skipOffset,
        clickThrough,
        clickTracking,
        impressions,
        errors,
        trackingEvents,
        loadedAt: Date.now(),
        used: false,
        wrapperDepth: depth,
        wrapperChain: [],
        urlTested: true // MARQUER COMME TESTÉE
      };
      
    } catch (error) {
      console.warn(`Erreur parsing publicité inline ${adId}:`, error.message);
      return null;
    }
  };

  // Tester et préparer une URL vidéo
  const testAndPrepareVideoUrl = async (originalUrl, adId, networkName) => {
    if (!originalUrl) {
      throw new Error('Pas d\'URL vidéo fournie');
    }

    // Vérifier le cache d'abord
    const cacheKey = `video_${originalUrl}`;
    if (urlTestCacheRef.current.has(cacheKey)) {
      const cachedResult = urlTestCacheRef.current.get(cacheKey);
      console.log(`💾 Cache hit URL vidéo pour ${adId}:`, originalUrl.substring(0, 50) + '...');
      
      if (cachedResult.success) {
        return cachedResult.finalUrl;
      } else {
        throw new Error(cachedResult.error);
      }
    }

    console.log(`🔍 Test URL vidéo pour ${adId} (${networkName}):`, originalUrl);

    try {
      // Détecter le type d'environnement
      const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent.toLowerCase());
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      let finalUrl;

      // Stratégie : mobile/localhost = proxy direct, desktop = test puis proxy si échec
      if (isMobile || isLocalhost) {
        console.log(`📱 Mobile/localhost détecté → proxy direct pour ${adId}`);
        finalUrl = `https://api.peupleqc.com/proxyVideo?url=${encodeURIComponent(originalUrl)}`;
      } else {
        // Desktop : tester URL directe
        const isExternalUrl = !originalUrl.includes('peupleqc.com') && 
                             !originalUrl.startsWith('data:') && 
                             !originalUrl.startsWith('blob:');

        if (isExternalUrl) {
          try {
            console.log(`🔄 Test CORS pour ${adId}...`);
            const testResponse = await fetch(originalUrl, { 
              method: 'HEAD', 
              mode: 'cors',
              cache: 'no-cache'
            });
            
            if (testResponse.ok) {
              console.log(`✅ CORS OK pour ${adId} → utilisation directe`);
              finalUrl = originalUrl;
            } else {
              throw new Error('CORS failed');
            }
          } catch (corsError) {
            console.log(`🔄 CORS échec pour ${adId} → fallback proxy`);
            finalUrl = `https://api.peupleqc.com/proxyVideo?url=${encodeURIComponent(originalUrl)}`;
          }
        } else {
          console.log(`✅ URL interne pour ${adId} → utilisation directe`);
          finalUrl = originalUrl;
        }
      }

      // Mettre en cache le succès
      urlTestCacheRef.current.set(cacheKey, { 
        success: true, 
        finalUrl, 
        timestamp: Date.now(),
        adId 
      });

      console.log(`✅ URL finale préparée pour ${adId}:`, finalUrl);
      return finalUrl;

    } catch (error) {
      console.error(`❌ Erreur préparation URL pour ${adId}:`, error.message);
      
      // Mettre en cache l'échec
      urlTestCacheRef.current.set(cacheKey, { 
        success: false, 
        error: error.message, 
        timestamp: Date.now(),
        adId 
      });

      // Fallback final vers proxy
      const proxyUrl = `https://api.peupleqc.com/proxyVideo?url=${encodeURIComponent(originalUrl)}`;
      console.log(`🔄 Fallback final proxy pour ${adId}:`, proxyUrl);
      
      return proxyUrl;
    }
  };

  // Cache pour éviter les tests répétés d'URLs
  const urlTestCacheRef = useRef(new Map());

  // Charger VAST via iframe pour éviter CORS
  const loadVastViaIframe = (vastUrl, networkName) => {
    return new Promise((resolve, reject) => {
      // Vérifier le cache d'abord
      const cacheKey = `${vastUrl}_${networkName}`;
      if (urlTestCacheRef.current.has(cacheKey)) {
        const cachedResult = urlTestCacheRef.current.get(cacheKey);
        console.log(`💾 Cache hit pour ${networkName}:`, vastUrl.substring(0, 50) + '...');
        
        if (cachedResult.success) {
          resolve(cachedResult.data);
        } else {
          reject(new Error(cachedResult.error));
        }
        return;
      }

      console.log(`📡 Chargement VAST depuis ${networkName}:`, vastUrl.substring(0, 100) + '...');
      
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.width = '1px';
      iframe.style.height = '1px';
      
      const timeout = setTimeout(() => {
        cleanup();
        const error = new Error(`Timeout (${adSettings.timeout}ms)`);
        // Mettre en cache l'échec pour éviter de répéter
        urlTestCacheRef.current.set(cacheKey, { success: false, error: error.message, timestamp: Date.now() });
        reject(error);
      }, adSettings.timeout);
      
      const cleanup = () => {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
        clearTimeout(timeout);
        window.removeEventListener('message', messageHandler);
      };
      
      const messageHandler = (event) => {
        if (event.source === iframe.contentWindow) {
          cleanup();
          
          if (event.data && event.data.error) {
            const error = new Error(event.data.error);
            // Mettre en cache l'échec
            urlTestCacheRef.current.set(cacheKey, { success: false, error: event.data.error, timestamp: Date.now() });
            reject(error);
          } else if (event.data && event.data.vastXml) {
            console.log(`✅ VAST reçu de ${networkName}, taille:`, event.data.vastXml.length);
            // Mettre en cache le succès
            urlTestCacheRef.current.set(cacheKey, { success: true, data: event.data.vastXml, timestamp: Date.now() });
            resolve(event.data.vastXml);
          } else {
            const error = new Error('Pas de données VAST reçues');
            urlTestCacheRef.current.set(cacheKey, { success: false, error: error.message, timestamp: Date.now() });
            reject(error);
          }
        }
      };
      
      window.addEventListener('message', messageHandler);
      
      // HTML à charger dans l'iframe pour gérer les data: URLs et les URLs normales
      // IMPORTANT: Supprimer les retry automatiques pour éviter les tests multiples
      const iframeContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
          </head>
          <body>
            <script>
              (function() {
                const vastUrl = '${vastUrl}';
                
                // Si c'est une data: URL, l'extraire directement
                if (vastUrl.startsWith('data:text/xml,')) {
                  try {
                    const xmlContent = decodeURIComponent(vastUrl.substring('data:text/xml,'.length));
                    parent.postMessage({ vastXml: xmlContent }, '*');
                    return;
                  } catch (error) {
                    parent.postMessage({ 
                      error: '${networkName}: Erreur décodage data URL - ' + error.message 
                    }, '*');
                    return;
                  }
                }
                
                // Faire un seul fetch sans retry automatique
                fetch(vastUrl, {
                  method: 'GET',
                  mode: 'cors',
                  credentials: 'omit',
                  cache: 'no-cache'
                })
                .then(function(response) {
                  if (!response.ok) {
                    throw new Error('HTTP ' + response.status + ': ' + response.statusText);
                  }
                  return response.text();
                })
                .then(function(xml) {
                  if (xml && xml.trim().length > 0) {
                    parent.postMessage({ vastXml: xml }, '*');
                  } else {
                    throw new Error('Réponse XML vide');
                  }
                })
                .catch(function(error) {
                  parent.postMessage({ 
                    error: '${networkName}: ' + error.message 
                  }, '*');
                });
              })();
            </script>
          </body>
        </html>
      `;
      
      iframe.src = 'data:text/html;charset=utf-8,' + encodeURIComponent(iframeContent);
      document.body.appendChild(iframe);
    });
  };

  // Mettre à jour le statut d'un réseau
  const updateNetworkStatus = useCallback((networkName, success, error = null) => {
    setNetworkStatus(prev => {
      const current = prev[networkName] || {};
      const updated = {
        ...current,
        lastSuccess: success ? Date.now() : current.lastSuccess,
        lastError: error ? Date.now() : current.lastError,
        consecutiveErrors: success ? 0 : (current.consecutiveErrors || 0) + 1,
        totalRequests: (current.totalRequests || 0) + 1,
        successfulRequests: success ? (current.successfulRequests || 0) + 1 : (current.successfulRequests || 0)
      };
      
      // Désactiver temporairement le réseau après trop d'erreurs
      if (updated.consecutiveErrors >= 3) {
        updated.enabled = false;
        console.warn(`🚫 Réseau ${networkName} désactivé après ${updated.consecutiveErrors} erreurs consécutives`);
      }
      
      return {
        ...prev,
        [networkName]: updated
      };
    });
  }, []);

  // Charger une publicité depuis un réseau spécifique
  const loadAdFromNetwork = useCallback(async (network) => {
    const now = Date.now();
    const lastRequest = lastRequestTimes[network.name] || 0;
    
    // Vérifier le délai minimum entre requêtes
    if (now - lastRequest < adSettings.minTimeBetweenRequests) {
      console.log(`⏰ ${network.name}: Trop tôt (${Math.round((adSettings.minTimeBetweenRequests - (now - lastRequest)) / 1000)}s restant)`);
      return null;
    }
    
    try {
      // Mettre à jour le temps de dernière requête
      setLastRequestTimes(prev => ({ ...prev, [network.name]: now }));
      
      // Charger le VAST avec résolution des wrappers
      const adsData = await loadVastWithWrappers(network.url, network.name, 0);
      
      if (adsData && adsData.length > 0) {
        console.log(`✅ ${network.name}: ${adsData.length} publicité(s) chargée(s) (avec wrappers résolus)`);
        
        // Log des détails des wrappers
        adsData.forEach(ad => {
          if (ad.wrapperChain && ad.wrapperChain.length > 0) {
            console.log(`🔗 Pub ${ad.id}: chaîne wrapper [${ad.wrapperChain.join(' -> ')}], tracking events: ${Object.keys(ad.trackingEvents).length}`);
          }
        });
        
        updateNetworkStatus(network.name, true);
        return adsData;
      } else {
        throw new Error('Aucune publicité valide trouvée');
      }
      
    } catch (error) {
      console.error(`❌ ${network.name}: ${error.message}`);
      updateNetworkStatus(network.name, false, error.message);
      return null;
    }
  }, [adSettings.minTimeBetweenRequests, lastRequestTimes, updateNetworkStatus]);

  // Charger des publicités depuis tous les réseaux disponibles
  const loadAdsFromAllNetworks = useCallback(async () => {
    const activeNetworks = getActiveNetworks();
    
    if (activeNetworks.length === 0) {
      console.warn('🚫 Aucun réseau publicitaire actif');
      return [];
    }
    
    console.log(`🔄 Chargement depuis ${activeNetworks.length} réseaux:`, activeNetworks.map(n => n.name).join(', '));
    
    const loadPromises = activeNetworks.map(network => 
      loadAdFromNetwork(network).catch(error => {
        console.error(`❌ Erreur réseau ${network.name}:`, error.message);
        return null;
      })
    );
    
    try {
      // Attendre toutes les requêtes avec un timeout global
      const results = await Promise.allSettled(loadPromises);
      
      const allAds = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          allAds.push(...result.value);
        }
      });
      
      if (allAds.length > 0) {
        console.log(`✅ Total: ${allAds.length} publicités chargées depuis tous les réseaux`);
        
        // Statistiques des wrappers
        const withWrappers = allAds.filter(ad => ad.wrapperChain && ad.wrapperChain.length > 0);
        if (withWrappers.length > 0) {
          console.log(`🔗 ${withWrappers.length} publicités avec wrappers résolus`);
        }
      } else {
        console.warn('⚠️ Aucune publicité chargée depuis aucun réseau');
      }
      
      return allAds;
      
    } catch (error) {
      console.error('❌ Erreur lors du chargement depuis tous les réseaux:', error.message);
      return [];
    }
  }, [getActiveNetworks, loadAdFromNetwork]);

  // **NOUVEAU: Vérifier si on peut retry pour cache vide**
  const canRetryForEmptyCache = useCallback(() => {
    const now = Date.now();
    const availableAds = preloadedAds.filter(ad => !ad.used);
    
    // Si on a des pubs, pas besoin de retry
    if (availableAds.length > 0) {
      return false;
    }
    
    // Si pas de dernière tentative vide, permettre
    if (!lastEmptyCacheTime) {
      return true;
    }
    
    // Vérifier le délai depuis la dernière tentative
    const timeSinceLastEmpty = now - lastEmptyCacheTime;
    return timeSinceLastEmpty >= adSettings.emptyRetryDelay;
  }, [preloadedAds, lastEmptyCacheTime, adSettings.emptyRetryDelay]);

  // **NOUVEAU: Vérifier si au moins un réseau peut faire une requête**
  const hasNetworkReadyForRequest = useCallback(() => {
    const activeNetworks = getActiveNetworks();
    const now = Date.now();
    
    return activeNetworks.some(network => {
      const lastRequest = lastRequestTimes[network.name] || 0;
      const timeSinceLastRequest = now - lastRequest;
      return timeSinceLastRequest >= adSettings.minTimeBetweenRequests;
    });
  }, [getActiveNetworks, lastRequestTimes, adSettings.minTimeBetweenRequests]);

  // Pré-charger des publicités
  const preloadAds = useCallback(async () => {
    if (isPreloading) {
      console.log('🔄 Pré-chargement déjà en cours...');
      return;
    }
    
    const availableAds = preloadedAds.filter(ad => !ad.used);
    
    // **MODIFICATION: Permettre le rechargement même si cache plein pour les cas de cache vide**
    const shouldAllowReload = availableAds.length === 0 && canRetryForEmptyCache() && hasNetworkReadyForRequest();
    
    if (availableAds.length >= adSettings.maxPreload && !shouldAllowReload) {
      console.log(`📦 Cache plein (${availableAds.length}/${adSettings.maxPreload})`);
      return;
    }
    
    setIsPreloading(true);
    
    try {
      console.log(`🚀 Démarrage pré-chargement (cache: ${availableAds.length}/${adSettings.maxPreload}, force reload: ${shouldAllowReload})`);
      
      // **NOUVEAU: Marquer la tentative pour cache vide**
      if (availableAds.length === 0) {
        setLastEmptyCacheTime(Date.now());
        console.log('📊 Cache vide détecté, marquage tentative de rechargement');
      }
      
      const newAds = await loadAdsFromAllNetworks();
      
      if (newAds.length > 0) {
        setPreloadedAds(prev => {
          // **NOUVELLE LOGIQUE: Filtrer les publicités avec URLs en cooldown**
          const existingIds = new Set(prev.map(ad => ad.id));
          const uniqueNewAds = newAds.filter(ad => {
            // Vérifier si l'ID existe déjà
            if (existingIds.has(ad.id)) {
              return false;
            }
            
            // **NOUVEAU: Vérifier si l'URL vidéo est en cooldown**
            if (ad.videoUrl && !canUseVideoUrl(ad.videoUrl)) {
              console.log(`🚫 Pub ${ad.id} ignorée - URL en cooldown:`, ad.videoUrl.substring(0, 50) + '...');
              return false;
            }
            
            return true;
          });
          
          const combined = [...prev, ...uniqueNewAds];
          
          // Garder seulement les plus récentes si on dépasse la limite
          if (combined.length > adSettings.maxPreload * 2) {
            return combined
              .sort((a, b) => b.loadedAt - a.loadedAt)
              .slice(0, adSettings.maxPreload * 2);
          }
          
          return combined;
        });
        
        console.log(`✅ ${newAds.length} nouvelles publicités ajoutées au cache`);
        
        // **NOUVEAU: Reset du timer de cache vide si succès**
        if (availableAds.length === 0) {
          setLastEmptyCacheTime(null);
          console.log('🔄 Cache vide résolu, reset du timer');
        }
      } else {
        // **NOUVEAU: Si échec et cache vide, garder le timestamp pour retry plus tard**
        if (availableAds.length === 0) {
          console.log('⚠️ Échec rechargement cache vide, retry dans', adSettings.emptyRetryDelay / 1000, 'secondes');
        }
      }
      
    } catch (error) {
      console.error('❌ Erreur pré-chargement:', error.message);
    } finally {
      setIsPreloading(false);
    }
  }, [isPreloading, preloadedAds, adSettings.maxPreload, adSettings.emptyRetryDelay, loadAdsFromAllNetworks, canRetryForEmptyCache, hasNetworkReadyForRequest, canUseVideoUrl]);

  // **NOUVEAU: Timer automatique pour retry quand cache vide**
  useEffect(() => {
    const availableAds = preloadedAds.filter(ad => !ad.used);
    
    // Si cache vide et pas de timer en cours
    if (availableAds.length === 0 && !emptyRetryTimer && !isPreloading) {
      console.log('📭 Cache vide détecté, programmation retry automatique dans', adSettings.emptyRetryDelay / 1000, 'secondes');
      
      const timer = setTimeout(() => {
        console.log('⏰ Timer cache vide déclenché, tentative de rechargement...');
        
        // Vérifier à nouveau si on peut faire des requêtes
        if (hasNetworkReadyForRequest()) {
          preloadAds();
        } else {
          console.log('⏰ Aucun réseau prêt, report du retry');
          // Programmer un nouveau timer plus court pour réessayer
          const shortTimer = setTimeout(() => {
            setEmptyRetryTimer(null);
          }, 10000); // 10 secondes
          setEmptyRetryTimer(shortTimer);
          return;
        }
        
        setEmptyRetryTimer(null);
      }, adSettings.emptyRetryDelay);
      
      setEmptyRetryTimer(timer);
    }
    
    // Si on a des pubs et un timer en cours, l'annuler
    if (availableAds.length > 0 && emptyRetryTimer) {
      console.log('✅ Cache rempli, annulation du timer de retry');
      clearTimeout(emptyRetryTimer);
      setEmptyRetryTimer(null);
      setLastEmptyCacheTime(null);
    }
    
    return () => {
      if (emptyRetryTimer) {
        clearTimeout(emptyRetryTimer);
      }
    };
  }, [preloadedAds.length, emptyRetryTimer, isPreloading, adSettings.emptyRetryDelay, hasNetworkReadyForRequest, preloadAds]);

  // Obtenir une publicité disponible
  const getAvailableAd = useCallback(() => {
    const availableAds = preloadedAds.filter(ad => !ad.used);
    
    if (availableAds.length === 0) {
      console.log('📭 Aucune publicité disponible dans le cache');
      
      // **NOUVEAU: Déclencher un pré-chargement d'urgence seulement si on peut**
      if (canRetryForEmptyCache() && hasNetworkReadyForRequest()) {
        console.log('🆘 Déclenchement pré-chargement d\'urgence');
        setTimeout(() => preloadAds(), 1000);
      } else {
        console.log('⏳ Pré-chargement d\'urgence impossible (délais réseau)');
      }
      
      return null;
    }
    
    // **NOUVELLE LOGIQUE: Filtrer les publicités avec URLs en cooldown**
    const usableAds = availableAds.filter(ad => {
      if (!ad.videoUrl) return false;
      return canUseVideoUrl(ad.videoUrl);
    });
    
    if (usableAds.length === 0) {
      console.log('🚫 Aucune publicité utilisable (toutes en cooldown)');
      
      // Déclencher un pré-chargement pour essayer d'obtenir de nouvelles pubs
      if (canRetryForEmptyCache() && hasNetworkReadyForRequest()) {
        console.log('🔄 Déclenchement pré-chargement - toutes les pubs en cooldown');
        setTimeout(() => preloadAds(), 2000);
      }
      
      return null;
    }
    
    // Prendre la publicité la plus récente du réseau avec le meilleur statut
    const bestAd = usableAds
      .sort((a, b) => {
        const statusA = networkStatus[a.network];
        const statusB = networkStatus[b.network];
        
        if (statusA && statusB) {
          const successRateA = statusA.totalRequests > 0 ? statusA.successfulRequests / statusA.totalRequests : 0;
          const successRateB = statusB.totalRequests > 0 ? statusB.successfulRequests / statusB.totalRequests : 0;
          
          if (successRateB !== successRateA) {
            return successRateB - successRateA;
          }
        }
        
        return b.loadedAt - a.loadedAt; // Plus récent en premier
      })[0];
    
    // **NOUVEAU: Marquer l'URL vidéo comme utilisée**
    if (bestAd.videoUrl) {
      markVideoUrlAsPlayed(bestAd.videoUrl);
    }
    
    // Marquer comme utilisée
    setPreloadedAds(prev => 
      prev.map(ad => 
        ad.id === bestAd.id 
          ? { ...ad, used: true, usedAt: Date.now() }
          : ad
      )
    );
    
    console.log(`📺 Publicité utilisée: ${bestAd.id} (${bestAd.network})`);
    
    // Log des informations de wrapper si présentes
    if (bestAd.wrapperChain && bestAd.wrapperChain.length > 0) {
      console.log(`🔗 Chaîne wrapper: [${bestAd.wrapperChain.join(' -> ')}]`);
      console.log(`📊 Tracking events combinés: ${Object.keys(bestAd.trackingEvents).length}`);
      console.log(`📈 Impressions combinées: ${bestAd.impressions.length}`);
    }
    
    // Déclencher le pré-chargement si le cache devient faible
    const remainingUsableAds = usableAds.length - 1;
    if (remainingUsableAds <= 1) {
      setTimeout(() => preloadAds(), 2000);
    }
    
    return bestAd;
  }, [preloadedAds, networkStatus, preloadAds, canRetryForEmptyCache, hasNetworkReadyForRequest, canUseVideoUrl, markVideoUrlAsPlayed]);

  // Vérifier si une publicité doit être affichée
  const shouldShowAd = (videoIndex) => {
    return videoIndex > 0 && videoIndex % adSettings.frequency === 0;
  };

  // Nettoyer les anciennes publicités et le cache d'URLs
  const cleanupOldAds = useCallback(() => {
    const maxAge = 30 * 60 * 1000; // 30 minutes
    const now = Date.now();
    
    setPreloadedAds(prev => {
      const cleaned = prev.filter(ad => (now - ad.loadedAt) < maxAge);
      const removed = prev.length - cleaned.length;
      
      if (removed > 0) {
        console.log(`🧹 ${removed} publicités anciennes supprimées du cache`);
      }
      
      return cleaned;
    });

    // Nettoyer aussi le cache des URLs testées (garder seulement les 10 minutes récentes)
    const urlCacheMaxAge = 10 * 60 * 1000; // 10 minutes
    const urlCache = urlTestCacheRef.current;
    let urlsCleaned = 0;
    
    for (const [key, value] of urlCache.entries()) {
      if (now - value.timestamp > urlCacheMaxAge) {
        urlCache.delete(key);
        urlsCleaned++;
      }
    }
    
    if (urlsCleaned > 0) {
      console.log(`🧹 ${urlsCleaned} URLs testées supprimées du cache`);
    }
    
    // **NOUVEAU: Nettoyer le cache des URLs jouées**
    cleanupPlayedUrls();
  }, [cleanupPlayedUrls]);

  // Fonction pour vider manuellement le cache des URLs (utile pour le debug)
  const clearUrlCache = useCallback(() => {
    urlTestCacheRef.current.clear();
    console.log('🗑️ Cache des URLs vidées manuellement');
  }, []);

  // **NOUVEAU: Fonction pour vider le cache des URLs jouées**
  const clearPlayedUrlsCache = useCallback(() => {
    playedVideoUrlsRef.current.clear();
    console.log('🗑️ Cache des URLs jouées vidé manuellement');
  }, []);

  // Fonction pour faire le tracking des événements (mise à jour pour gérer les wrappers)
  const fireTracking = useCallback((urls, eventType = 'unknown') => {
    if (!urls || urls.length === 0) return;
    
    console.log(`📊 Tracking ${eventType}:`, urls.length, 'URLs');
    
    // Dédupliquer les URLs pour éviter les doublons
    const uniqueUrls = [...new Set(urls.filter(url => url && url.trim()))];
    
    uniqueUrls.forEach((url, index) => {
      // Remplacer les macros communes
      const finalUrl = url
        .replace(/\[ERRORCODE\]/g, '')
        .replace(/\[TIMESTAMP\]/g, Date.now())
        .replace(/\[CACHEBUSTING\]/g, Math.random().toString(36).substr(2, 9));
      
      // Fire tracking pixel avec un petit délai pour éviter la surcharge
      setTimeout(() => {
        fetch(finalUrl, { 
          method: 'GET',
          mode: 'no-cors'
        }).catch(() => {
          // Ignorer les erreurs de tracking
        });
      }, index * 50); // Décalage de 50ms entre chaque URL
    });
    
    if (uniqueUrls.length !== urls.length) {
      console.log(`🔄 ${urls.length - uniqueUrls.length} URLs dupliquées filtrées pour ${eventType}`);
    }
  }, []);

  // Obtenir les statistiques des réseaux
  const getNetworkStats = useCallback(() => {
    return Object.entries(networkStatus).map(([name, status]) => ({
      name,
      ...status,
      successRate: status.totalRequests > 0 ? 
        Math.round((status.successfulRequests / status.totalRequests) * 100) : 0,
      lastRequestTime: lastRequestTimes[name] || 0
    }));
  }, [networkStatus, lastRequestTimes]);

  // Réactiver tous les réseaux
  const resetNetworks = useCallback(() => {
    setNetworkStatus(prev => {
      const reset = {};
      Object.keys(prev).forEach(name => {
        reset[name] = {
          ...prev[name],
          consecutiveErrors: 0,
          enabled: true
        };
      });
      return reset;
    });
    
    console.log('🔄 Tous les réseaux publicitaires ont été réactivés');
  }, []);

  // Obtenir les statistiques des wrappers
  const getWrapperStats = useCallback(() => {
    const adsWithWrappers = preloadedAds.filter(ad => ad.wrapperChain && ad.wrapperChain.length > 0);
    const totalWrapperDepth = adsWithWrappers.reduce((sum, ad) => sum + (ad.wrapperDepth || 0), 0);
    
    return {
      totalAds: preloadedAds.length,
      adsWithWrappers: adsWithWrappers.length,
      averageWrapperDepth: adsWithWrappers.length > 0 ? (totalWrapperDepth / adsWithWrappers.length).toFixed(1) : 0,
      maxWrapperDepth: Math.max(...adsWithWrappers.map(ad => ad.wrapperDepth || 0), 0),
      wrapperChains: adsWithWrappers.map(ad => ({
        adId: ad.id,
        network: ad.network,
        depth: ad.wrapperDepth,
        chain: ad.wrapperChain.join(' -> '),
        trackingEventsCount: Object.keys(ad.trackingEvents).length,
        impressionsCount: ad.impressions.length
      }))
    };
  }, [preloadedAds]);

  // **NOUVEAU: Obtenir les statistiques des URLs jouées**
  const getPlayedUrlsStats = useCallback(() => {
    const now = Date.now();
    const playedUrls = Array.from(playedVideoUrlsRef.current.entries()).map(([url, timestamp]) => ({
      url: url.substring(0, 50) + '...',
      fullUrl: url,
      playedAt: timestamp,
      remainingCooldown: Math.max(0, Math.ceil((adSettings.videoUrlCooldown - (now - timestamp)) / 1000))
    }));
    
    return {
      totalPlayedUrls: playedUrls.length,
      urlsInCooldown: playedUrls.filter(item => item.remainingCooldown > 0).length,
      playedUrls: playedUrls.sort((a, b) => b.playedAt - a.playedAt), // Plus récentes en premier
      cooldownDuration: adSettings.videoUrlCooldown / 1000 // en secondes
    };
  }, [adSettings.videoUrlCooldown]);

  // **NOUVEAU: Fonction pour forcer le rechargement (utile pour debug)**
  const forceReload = useCallback(() => {
    console.log('🔄 Rechargement forcé des publicités');
    setLastEmptyCacheTime(null);
    if (emptyRetryTimer) {
      clearTimeout(emptyRetryTimer);
      setEmptyRetryTimer(null);
    }
    preloadAds();
  }, [preloadAds, emptyRetryTimer]);

  // Initialisation et nettoyage périodique
  useEffect(() => {
    // Pré-charger des publicités au démarrage
    const initTimeout = setTimeout(() => {
      preloadAds();
    }, 2000);
    
    // Nettoyer les anciennes pubs périodiquement
    const cleanupInterval = setInterval(cleanupOldAds, 5 * 60 * 1000); // Toutes les 5 minutes
    
    // Réactiver les réseaux périodiquement
    const resetInterval = setInterval(resetNetworks, 60 * 60 * 1000); // Toutes les heures
    
    return () => {
      clearTimeout(initTimeout);
      clearInterval(cleanupInterval);
      clearInterval(resetInterval);
      
      // **NOUVEAU: Nettoyer le timer de retry au démontage**
      if (emptyRetryTimer) {
        clearTimeout(emptyRetryTimer);
      }
    };
  }, [preloadAds, cleanupOldAds, resetNetworks, emptyRetryTimer]);

  // **MODIFIÉ: Surveillance du cache avec gestion du timer vide** 
  useEffect(() => {
    const availableAds = preloadedAds.filter(ad => !ad.used);
    
    // **NOUVEAU: Log détaillé du statut du cache**
    if (availableAds.length === 0) {
      console.log('📊 Cache vide détecté:', {
        totalAds: preloadedAds.length,
        usedAds: preloadedAds.filter(ad => ad.used).length,
        availableAds: availableAds.length,
        isPreloading,
        hasRetryTimer: !!emptyRetryTimer,
        lastEmptyTime: lastEmptyCacheTime,
        canRetry: canRetryForEmptyCache(),
        hasNetworkReady: hasNetworkReadyForRequest()
      });
    }
    
    if (availableAds.length <= 1 && availableAds.length > 0 && !isPreloading) {
      const timeout = setTimeout(() => {
        console.log('🔄 Cache faible, rechargement automatique...');
        preloadAds();
      }, 5000);
      
      return () => clearTimeout(timeout);
    }
  }, [preloadedAds, isPreloading, preloadAds, emptyRetryTimer, lastEmptyCacheTime, canRetryForEmptyCache, hasNetworkReadyForRequest]);

  return {
    // État
    preloadedAds,
    availableAds: preloadedAds.filter(ad => !ad.used),
    isPreloading,
    networkStatus,
    
    // **NOUVEAU: États pour le monitoring du cache vide**
    lastEmptyCacheTime,
    hasEmptyRetryTimer: !!emptyRetryTimer,
    canRetryForEmptyCache: canRetryForEmptyCache(),
    hasNetworkReadyForRequest: hasNetworkReadyForRequest(),
    
    // Fonctions principales
    getAvailableAd,
    shouldShowAd,
    preloadAds,
    fireTracking,
    
    // **NOUVEAU: Fonctions de gestion des URLs jouées**
    canUseVideoUrl,
    markVideoUrlAsPlayed,
    
    // Configuration
    adSettings,
    
    // Statistiques et contrôle
    getNetworkStats,
    getWrapperStats,
    getPlayedUrlsStats, // **NOUVEAU**
    resetNetworks,
    clearUrlCache,
    clearPlayedUrlsCache, // **NOUVEAU**
    forceReload,
    
    // Réseaux
    activeNetworks: getActiveNetworks(),
    
    // Cache info (pour debug)
    urlCacheSize: urlTestCacheRef.current.size,
    playedUrlsCacheSize: playedVideoUrlsRef.current.size // **NOUVEAU**
  };
};

export { useAdManager, VastAdManager };